import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import TicketIcon from "../../../assets/aftersalesservice_icons/ticket_icon.svg";
import NewTickteIcon from "../../../assets/aftersalesservice_icons/new_ticket.svg";
import OngoingIcon from "../../../assets/aftersalesservice_icons/ongoing.svg";
import SolvedIcon from "../../../assets/aftersalesservice_icons/solved.svg";
import ApiService from "../../../components/Api_services";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const MyTicket = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [sn1, setSn1] = useState(null);
  const [sn2, setSn2] = useState(null);
  const [newList, setNewList] = useState([]);
  const [ongoingList, setOngoingList] = useState([]);
  const [solvedList, setSolvedList] = useState([]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);
        // console.log("warranty info:", response);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest,
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );

          // Extract serial numbers from warranty_item_list
          const foundSerials = normalizedItems
            .map((item) => item?.serial_no?.trim())
            .filter((s) => s && s !== "");

          if (foundSerials.length >= 2) {
            setSn1(foundSerials[0]);
            setSn2(foundSerials[1]);
          } else if (foundSerials.length === 1) {
            setSn1(foundSerials[0]);
            setSn2(foundSerials[0]);
          } else {
            setSn1(serialNo);
            setSn2(serialNo);
          }
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
        // Fallback to original serial on error
        const serialNo = getSerialNo();
        setSn1(serialNo);
        setSn2(serialNo);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  useEffect(() => {
    const fetchTicketList = async () => {
      if (!sn1 || !sn2) return;

      const payload = {
        serial_no_1: sn1,
        serial_no_2: sn2,
      };

      try {
        const response = await ApiService.getTicketList(payload);

        if (response && response.status === 200) {
          const rows = response.data || [];

          const newT = [];
          const ongoingT = [];
          const solvedT = [];

          (Array.isArray(rows) ? rows : []).forEach((rw) => {
            if (rw?.status == null) return;
            const status = String(rw.status);

            if (status === "1") {
              newT.push(rw);
            } else if (["2", "3", "4"].includes(status)) {
              ongoingT.push(rw);
            } else if (status === "5") {
              solvedT.push(rw);
            }
          });

          setNewList(newT);
          setOngoingList(ongoingT);
          setSolvedList(solvedT);
        }
      } catch (error) {
        console.error("Error fetching ticket list:", error);
      }
    };

    fetchTicketList();
  }, [sn1, sn2]);

  const ResponseTimer = ({ start }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    }, []);

    let startTime = start ? new Date(start).getTime() : Date.now();
    if (isNaN(startTime)) startTime = Date.now();
    const diffSeconds = Math.max(0, Math.floor((now - startTime) / 1000));
    const FULL_DAY = 86400;
    const progress = Math.min((diffSeconds / FULL_DAY) * 100, 100);

    return (
      <div className="as-response-timer" data-start={start}>
        <div className="as-timer-label">
          <span>⏱ {trans.response_timer}</span>
          <span className="as-time-value">{formatTime(diffSeconds)}</span>
        </div>
        <div className="as-progress-bar">
          <div className="as-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  };

  const handleCardClick = (ticketNo) => {
    navigate(
      `/aftersalesservice/myticketdetail${buildAfterSalesQuery(searchParams, {
        ticket_no: ticketNo,
      })}`,
    );
  };

  return (
    <AfterSalesLayout>
      <div className="as-pre_page">
        <div className="as-page">
          <div className="as-post_page">
            <div className="as-header-card">
              <button
                className="as-back-btn"
                onClick={() => window.history.back()}
                aria-label="Go back"
              >
                <FallbackImg src={BackBlue} alt="back" />
                <span>{trans.back}</span>
              </button>

              <div className="as-title-section">
                <div className="as-card-title-text" style={{ display: "flex" }}>
                  <div style={{ paddingRight: 10 }}>
                    <FallbackImg
                      src={TicketIcon}
                      alt="ticket-icon"
                      className="as-ticket-icon"
                    />
                  </div>
                  <div>
                    <h2>{trans.my_tickets}</h2>
                    <p>{trans.my_tickets_sub}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="as-ticket-section">
              <div className="as-section-header">
                <span
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <FallbackImg src={NewTickteIcon} alt="new-ticket" />
                  {trans.new_tickets}
                </span>
                {newList && newList.length > 0 ? (
                  <span className="as-badge">{newList.length}</span>
                ) : null}
              </div>

              {newList.map((rw, i) => (
                <div
                  key={i}
                  className="as-ticket-card"
                  data-type="new"
                  data-ticket={rw.ticket_no}
                  onClick={() => handleCardClick(rw.ticket_no)}
                >
                  <div className="as-ticket-number">#{rw.ticket_no}</div>
                  <div className="as-ticket-type">{rw.type}</div>
                  <div className="as-ticket-status">
                    <span>⏱</span>
                    <span>{trans.our_sup_3}</span>
                  </div>
                  <div className="as-ticket-date">
                    {new Date(rw.inserted_date).toLocaleDateString()}
                  </div>

                  <ResponseTimer start={rw.inserted_date} />
                </div>
              ))}
            </div>

            <div className="as-ticket-section">
              <div className="as-section-header">
                <span
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <FallbackImg src={OngoingIcon} alt="ongoing" />
                  {trans.ongoing_tickets}
                </span>
                {ongoingList && ongoingList.length > 0 ? (
                  <span className="as-badge">{ongoingList.length}</span>
                ) : null}
              </div>

              {ongoingList.map((rw, i) => (
                <div
                  key={i}
                  className="as-ticket-card as-ongoing"
                  data-type="ongoing"
                  data-ticket={rw.ticket_no}
                  onClick={() => handleCardClick(rw.ticket_no)}
                >
                  <div className="as-ticket-number">#{rw.ticket_no}</div>
                  <div className="as-ticket-type">{rw.type}</div>
                  <div className="as-ticket-status as-warning">
                    <FallbackImg
                      src={OngoingIcon}
                      alt="ongoing-icon"
                      style={{ height: 13 }}
                    />
                    <span>{trans.serv_agent}</span>
                  </div>
                  <div className="as-ticket-date">
                    {new Date(rw.inserted_date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>

            <div className="as-ticket-section">
              <div className="as-section-header">
                <span
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <FallbackImg src={SolvedIcon} alt="solved" />
                  {trans.solved_tickets}
                </span>
                {solvedList && solvedList.length > 0 ? (
                  <span className="as-badge">{solvedList.length}</span>
                ) : null}
              </div>

              {solvedList.map((rw, i) => (
                <div
                  key={i}
                  className="as-ticket-card"
                  data-type="solved"
                  data-ticket={rw.ticket_no}
                  onClick={() => handleCardClick(rw.ticket_no)}
                >
                  <div className="as-ticket-number">#{rw.ticket_no}</div>
                  <div className="as-ticket-type">{rw.type}</div>
                  <div className="as-ticket-status as-success">
                    <span>✓</span>
                    <span>{trans.ticket_resolved}</span>
                  </div>
                  <div className="as-ticket-date">
                    {new Date(rw.inserted_date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default MyTicket;
